import React from "react";

import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import ReadableProjectName from "../../components/ReadableProjectName.jsx";

/**
 * Renders a row for a specific year in the Engineering User Work Distribution table.
 *
 * @param {object} props - The component props.
 * @param {number} props.year - The year to display.
 * @param {object} props.stats - The statistics for the year.
 * @param {object} props.user - The user data.
 * @returns {React.ReactElement} The rendered component.
 */
function EngineeringUserYearRow({ year, stats, user }) {
  const displayData = React.useMemo(() => {
    if (!stats) {
      return [];
    }

    const data = [];

    // Calculate total expected working hours
    let totalWorkingHours = stats.totalHours;
    if (user.weeklyJiraHours && user.weeklyJiraHours > 0 && user.weeklyWorkingHours) {
      totalWorkingHours = stats.totalHours * (user.weeklyWorkingHours / user.weeklyJiraHours);
    }

    // Projects
    stats.projects.forEach((project) => {
      data.push({
        key: project.id,
        label: <ReadableProjectName name={project.name} jiraId={project.jiraId} />,
        hours: project.hours,
        percentage: totalWorkingHours ? (project.hours / totalWorkingHours) * 100 : 0,
      });
    });

    // Unassigned (ECOs and Change Requests)
    if (stats.unassigned > 0) {
      data.push({
        key: "unassigned",
        label: <Typography sx={{ fontStyle: "italic" }}>ECOs and Change Requests</Typography>,
        hours: stats.unassigned,
        percentage: totalWorkingHours ? (stats.unassigned / totalWorkingHours) * 100 : 0,
      });
    }

    // Non-Jira Work
    const nonJiraHours = totalWorkingHours - stats.totalHours;
    if (nonJiraHours > 0.1) {
      data.push({
        key: "non-jira",
        label: <Typography sx={{ fontStyle: "italic" }}>Non-Jira Work</Typography>,
        hours: nonJiraHours,
        percentage: totalWorkingHours ? (nonJiraHours / totalWorkingHours) * 100 : 0,
      });
    }

    // Sort by percentage descending
    return data.toSorted((a, b) => b.hours - a.hours);
  }, [stats, user.weeklyJiraHours, user.weeklyWorkingHours]);

  if (!stats) {
    return null;
  }

  return (
    <>
      <Grid container spacing={2} sx={{ py: 1, alignItems: "center" }}>
        <Grid size="auto">
          <Typography
            variant="body1"
            sx={{
              fontWeight: "bold",
            }}
          >
            {year}
          </Typography>
        </Grid>
        <Grid size="grow">
          {displayData.length === 0 ? (
            <Typography variant="body2" sx={{ fontStyle: "italic", color: "text.secondary" }}>
              No work recorded
            </Typography>
          ) : (
            <Stack spacing={1}>
              {displayData.map((item) => (
                <Grid container key={item.key} spacing={2} sx={{ alignItems: "center" }}>
                  <Grid size={{ xs: "grow", md: 4 }}>{item.label}</Grid>
                  <Grid size="grow" sx={{ display: { xs: "none", md: "block" } }}>
                    <LinearProgress
                      variant="determinate"
                      value={item.percentage}
                      sx={{ height: 10, borderRadius: 5 }}
                    />
                  </Grid>
                  <Grid size={{ xs: 4, md: 2 }}>
                    <Typography variant="body2" align="center">
                      {item.percentage.toFixed(1)}%
                    </Typography>
                  </Grid>
                  <Grid size={2} sx={{ display: { xs: "none", md: "block" } }}>
                    <Typography variant="body2" align="center">
                      {Math.round(item.hours)}h
                    </Typography>
                  </Grid>
                </Grid>
              ))}
            </Stack>
          )}
        </Grid>
      </Grid>
      <Divider variant="middle" sx={{ mx: -1 }} />
    </>
  );
}

export default EngineeringUserYearRow;
